import { Box, Flex, IconButton, Tooltip, useColorModeValue } from '@chakra-ui/react';
import { useQueryClient, useIsFetching } from '@tanstack/react-query';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';

import type { Address } from 'types/api/address';

import { getResourceKey } from 'lib/api/useApiQuery';
import useIsMobile from 'lib/hooks/useIsMobile';
import { useI18n } from 'lib/i18n/context';
import * as mixpanel from 'lib/mixpanel/index';
import getQueryParamString from 'lib/router/getQueryParamString';
import Skeleton from 'ui/shared/chakra/Skeleton';
import IconSvg from 'ui/shared/IconSvg';

import { ADDRESS_TOKEN_KEYS } from '../i18n';
import useFetchTokens from '../utils/useFetchTokens';
import TokenSelectDesktop from './TokenSelectDesktop';
import TokenSelectMobile from './TokenSelectMobile';

interface Props {
  onClick?: () => void;
}

const TokenSelect = ({ onClick }: Props) => {
  const { t } = useI18n();
  const router = useRouter();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const addressHash = getQueryParamString(router.query.hash);
  const addressResourceKey = getResourceKey('address', { pathParams: { hash: addressHash } });

  const addressQueryData = queryClient.getQueryData<Address>(addressResourceKey);

  /** Prefer API `hash` (e.g. resolved ENS); fall back to URL so RQ is not disabled when cache is not hydrated yet. */
  const hashForTokens = addressQueryData?.hash || addressHash || undefined;

  const { data, isPending } = useFetchTokens({ hash: hashForTokens });
  const tokensResourceKey = getResourceKey('address_tokens', { pathParams: { hash: addressQueryData?.hash }, queryParams: { type: 'ERC-20' } });
  const tokensIsFetching = useIsFetching({ queryKey: tokensResourceKey });

  const walletBg = useColorModeValue('#FFFFFF', '#171717');
  const walletBorder = useColorModeValue('#E9ECEF', '#222222');
  const allTokensTooltipBg = useColorModeValue('gray.700', 'gray.100');
  const allTokensTooltipColor = useColorModeValue('white', 'gray.900');

  const handleIconButtonClick = React.useCallback(() => {
    mixpanel.logEvent(mixpanel.EventTypes.PAGE_WIDGET, { Type: 'Tokens show all (icon)' });
    onClick?.();
  }, [ onClick ]);

  if (isPending) {
    return (
      <Flex columnGap="8px">
        <Skeleton h={ 10 } w="180px" borderRadius="base"/>
        <Skeleton h={ 10 } w={ 10 } borderRadius="base"/>
      </Flex>
    );
  }

  return (
    <Flex columnGap="8px" mt={{ base: 1, lg: 0 }} width="100%">
      { isMobile ?
        <TokenSelectMobile data={ data } isLoading={ tokensIsFetching === 1 }/> : (
          <Box flex={ 1 } minW={ 0 }>
            <TokenSelectDesktop data={ data } isLoading={ tokensIsFetching === 1 }/>
          </Box>
        ) }
      <Tooltip
        label={ t(ADDRESS_TOKEN_KEYS.actions.showAllTokens) }
        bg={ allTokensTooltipBg }
        color={ allTokensTooltipColor }
        borderRadius="md"
        px={ 2 }
        py={ 1 }
        fontSize="sm"
        boxShadow="md"
        openDelay={ 300 }
      >
        <Box>
          <NextLink href={{ pathname: '/address/[hash]', query: { hash: addressHash, tab: 'tokens' } }} passHref legacyBehavior>
            <IconButton
              aria-label={ t(ADDRESS_TOKEN_KEYS.actions.showAllTokens) }
              as="a"
              onClick={ handleIconButtonClick }
              boxSize="40px"
              minW="40px"
              bg={ walletBg }
              border="1px solid"
              borderColor={ walletBorder }
              borderRadius="4px"
              variant="unstyled"
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{ borderColor: walletBorder }}
              icon={ <IconSvg name="empty-wallet" boxSize={ 5 }/> }
            />
          </NextLink>
        </Box>
      </Tooltip>
    </Flex>
  );
};

export default React.memo(TokenSelect);
